import { X } from "lucide-react";
import { useMemo, useState } from "react";

import type { Filter } from "./config";
import FilterChip from "./FilterChip";
import { PLAIN_SEARCH_CHIP_ID } from "./stores/structured-search/structured-search-store";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTruncatedList } from "@/hooks/useTruncatedList";
import { cn } from "@/lib/utils";

export type CompactChipItem =
  | { id: typeof PLAIN_SEARCH_CHIP_ID; kind: "plain-search"; label: string }
  | { id: string; kind: "filter"; filter: Filter };

export const CompactPlainSearchChip = ({
  label,
  onEdit,
  onRemove,
  selectedForRemoval = false,
}: {
  label: string;
  onEdit: () => void;
  onRemove: () => void;
  selectedForRemoval?: boolean;
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onEdit}
    onKeyDown={event => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onEdit();
      }
    }}
    className={cn(
      "inline-flex min-h-7 max-w-full cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 shadow-sm shadow-slate-200/60 transition-colors hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none",
      selectedForRemoval && "border-blue-300 bg-blue-50 text-blue-700 ring-offset-1",
    )}
  >
    <span className="truncate">
      Search text contains <span className="font-medium">"{label}"</span>
    </span>
    <button
      type="button"
      onClick={event => {
        event.stopPropagation();
        onRemove();
      }}
      className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-200/70 hover:text-slate-600"
      aria-label="Remove plain text search"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  </div>
);

interface CompactFilterChipItemProps {
  item: CompactChipItem;
  selectedFilterIdForRemoval: string | null;
  editingFilterId: string | null;
  isFilterLocked: (id: string) => boolean;
  onEditPlainSearch: () => void;
  onRemovePlainSearch: () => void;
  onEditFilter: (id: string) => void;
  onRemoveFilter: (id: string) => void;
}

const CompactFilterChipItem = ({
  item,
  selectedFilterIdForRemoval,
  editingFilterId,
  isFilterLocked,
  onEditPlainSearch,
  onRemovePlainSearch,
  onEditFilter,
  onRemoveFilter,
}: CompactFilterChipItemProps) => {
  if (item.kind === "plain-search") {
    return (
      <CompactPlainSearchChip
        label={item.label}
        onEdit={onEditPlainSearch}
        onRemove={onRemovePlainSearch}
        selectedForRemoval={selectedFilterIdForRemoval === item.id}
      />
    );
  }

  return (
    <FilterChip
      filter={item.filter}
      onRemove={() => onRemoveFilter(item.id)}
      onEdit={() => onEditFilter(item.id)}
      selectedForRemoval={selectedFilterIdForRemoval === item.id}
      isEditing={editingFilterId === item.id}
      isLocked={isFilterLocked(item.id)}
    />
  );
};

export const CompactFilterChips = ({
  items,
  selectedFilterIdForRemoval,
  editingFilterId,
  isFilterLocked,
  onEditPlainSearch,
  onRemovePlainSearch,
  onEditFilter,
  onRemoveFilter,
}: {
  items: CompactChipItem[];
  selectedFilterIdForRemoval: string | null;
  editingFilterId: string | null;
  isFilterLocked: (id: string) => boolean;
  onEditPlainSearch: () => void;
  onRemovePlainSearch: () => void;
  onEditFilter: (id: string) => void;
  onRemoveFilter: (id: string) => void;
}) => {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const recalcKey = useMemo(
    () =>
      items
        .map(item => (item.kind === "plain-search" ? `${item.id}:${item.label}` : item.id))
        .join("|"),
    [items],
  );
  const { containerRef, measureRef, visibleCount } = useTruncatedList({
    itemCount: items.length,
    gapPx: 8,
    overflowIndicatorWidth: 38,
    keepMinOne: true,
    recalcKey,
  });
  const effectiveVisibleCount = items.length > 0 ? Math.max(1, visibleCount) : 0;
  const visibleItems = items.slice(0, effectiveVisibleCount);
  const hiddenItems = items.slice(effectiveVisibleCount);
  const hiddenCount = hiddenItems.length;
  const handleOverflowMouseEnter = () => setOverflowOpen(true);

  const handleOverflowMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    const isOverflowTarget =
      event.relatedTarget instanceof HTMLElement &&
      Boolean(
        event.relatedTarget.closest(
          "[data-structured-search-overflow], [data-structured-search-overflow-trigger]",
        ),
      );

    if (isOverflowTarget) {
      return;
    }

    setOverflowOpen(false);
  };

  const chipItemProps = {
    selectedFilterIdForRemoval,
    editingFilterId,
    isFilterLocked,
    onEditPlainSearch,
    onRemovePlainSearch,
    onEditFilter,
    onRemoveFilter,
  };

  const renderChipItem = (item: CompactChipItem, className = "shrink-0") => (
    <span className={cn("min-w-0", className)} key={item.id}>
      <CompactFilterChipItem item={item} {...chipItemProps} />
    </span>
  );

  return (
    <div
      ref={containerRef}
      className="relative flex min-w-0 max-w-full flex-nowrap items-center justify-start gap-2 overflow-hidden"
    >
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute left-0 top-0 flex flex-nowrap items-center gap-2"
        aria-hidden="true"
      >
        {items.map(item => renderChipItem(item))}
      </div>
      {visibleItems.map((item, index) =>
        renderChipItem(item, index === 0 ? "max-w-full shrink overflow-hidden" : "shrink-0"),
      )}
      {hiddenCount > 0 ? (
        <Popover open={overflowOpen} onOpenChange={setOverflowOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-structured-search-overflow-trigger="true"
              onMouseEnter={handleOverflowMouseEnter}
              onMouseLeave={handleOverflowMouseLeave}
              className="inline-flex h-7 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-600 shadow-sm shadow-slate-200/60 transition-colors hover:border-slate-300 hover:bg-slate-50"
              aria-label={`Show ${hiddenCount} more filters`}
            >
              +{hiddenCount}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={0}
            className="z-[120] w-[346px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white p-0 shadow-xl shadow-slate-200/70"
            data-structured-search-overflow="true"
            onOpenAutoFocus={event => event.preventDefault()}
            onCloseAutoFocus={event => event.preventDefault()}
            onMouseEnter={handleOverflowMouseEnter}
            onMouseLeave={handleOverflowMouseLeave}
          >
            <div className="border-b border-slate-100 bg-slate-50/80 px-3.5 py-3">
              <div className="text-sm font-semibold text-slate-900">Hidden filters</div>
              <div className="mt-1 text-xs text-slate-500">
                {hiddenCount} more filter{hiddenCount === 1 ? "" : "s"} applied. Select one to edit
                it.
              </div>
            </div>
            <div className="flex max-h-56 flex-wrap gap-2 overflow-y-auto p-3">
              {hiddenItems.map(item => (
                <CompactFilterChipItem key={item.id} item={item} {...chipItemProps} />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
};
